import { useId, useMemo, useState } from "react";
import { ButtonSave } from "../ui";
import { computeFaqStatus, validateFaqItem } from "../../utils/faqCampaignStatus";
import styles from "./FaqCampaignEditor.module.css";

/**
 * FaqCampaignEditor — editor controlado de `faqItems` (FAQ perene + campanhas
 * datadas). Espelha o padrão de rascunho local do BusinessHoursEditor, mas com
 * seu próprio botão Salvar (o container é uma <section>, não um <form>).
 *
 * Props:
 *   @param {Array}    initialItems  itens vindos do backend (cru).
 *   @param {Function} onSave        callback(cleanArray) — array já saneado
 *                                   (sem campos internos; FAQ sem type/datas).
 *   @param {boolean}  saving        estado de loading do save (do pai).
 *
 * Cada item do rascunho carrega uma flag interna `_isNew` (NÃO enviada ao
 * backend) para a regra "validUntil >= hoje" valer só em itens novos.
 */

const TONE_CLASS = {
  neutral: "badgeNeutral",
  success: "badgeSuccess",
  warning: "badgeWarning",
  danger: "badgeDanger",
};

// Cria um item de rascunho vazio (FAQ por padrão).
function makeEmptyDraft() {
  return {
    question: "",
    answer: "",
    type: "faq",
    validFrom: "",
    validUntil: "",
    _isNew: true,
  };
}

// Normaliza os itens carregados do backend para o shape do rascunho.
function toDrafts(items) {
  if (!Array.isArray(items)) return [];
  return items.map((it) => ({
    question: it?.question ?? "",
    answer: it?.answer ?? "",
    type: it?.type === "campaign" ? "campaign" : "faq",
    validFrom: it?.validFrom ?? "",
    validUntil: it?.validUntil ?? "",
    _isNew: false,
  }));
}

// Converte o rascunho no payload limpo para o backend:
//   • remove `_isNew`;
//   • FAQ perene → só { question, answer } (sem type/datas);
//   • campanha  → { question, answer, type, validUntil, validFrom? }.
function toCleanItem(draft) {
  const base = {
    question: draft.question.trim(),
    answer: draft.answer.trim(),
  };
  if (draft.type === "campaign") {
    base.type = "campaign";
    base.validUntil = draft.validUntil;
    if (draft.validFrom) base.validFrom = draft.validFrom;
  }
  return base;
}

export default function FaqCampaignEditor({
  initialItems = [],
  onSave,
  saving = false,
}) {
  const uid = useId();
  const [drafts, setDrafts] = useState(() => toDrafts(initialItems));
  // Guarda o snapshot inicial (JSON) para o dirty-check.
  const [pristine] = useState(() => JSON.stringify(toDrafts(initialItems)));
  // Erros por índice: { [i]: { question?, answer?, validFrom?, validUntil? } }.
  const [errors, setErrors] = useState({});

  const isDirty = useMemo(
    () => JSON.stringify(drafts) !== pristine,
    [drafts, pristine],
  );

  // Atualização imutável de um item por índice.
  const patchItem = (index, patch) => {
    setDrafts((prev) =>
      prev.map((it, i) => (i === index ? { ...it, ...patch } : it)),
    );
  };

  const handleFieldChange = (index, field, value) => {
    patchItem(index, { [field]: value });
    // Limpa o erro do campo assim que o usuário edita.
    setErrors((prev) => {
      if (!prev[index]?.[field]) return prev;
      const nextForItem = { ...prev[index] };
      delete nextForItem[field];
      return { ...prev, [index]: nextForItem };
    });
  };

  const handleTypeChange = (index, nextType) => {
    if (nextType === "faq") {
      // Ao voltar para FAQ, limpamos datas (não trafegam) e erros de data.
      patchItem(index, { type: "faq", validFrom: "", validUntil: "" });
      setErrors((prev) => {
        if (!prev[index]) return prev;
        const nextForItem = { ...prev[index] };
        delete nextForItem.validFrom;
        delete nextForItem.validUntil;
        return { ...prev, [index]: nextForItem };
      });
    } else {
      patchItem(index, { type: "campaign" });
    }
  };

  const handleAdd = () => {
    setDrafts((prev) => [...prev, makeEmptyDraft()]);
  };

  const handleRemove = (index) => {
    setDrafts((prev) => prev.filter((_, i) => i !== index));
    // Reindexa os erros removendo o item excluído.
    setErrors((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        const i = Number(k);
        if (i < index) next[i] = prev[k];
        else if (i > index) next[i - 1] = prev[k];
      });
      return next;
    });
  };

  const handleSave = () => {
    const now = new Date();
    const nextErrors = {};
    drafts.forEach((draft, i) => {
      const { valid, errors: itemErrors } = validateFaqItem(draft, {
        isNew: draft._isNew === true,
        now,
      });
      if (!valid) nextErrors[i] = itemErrors;
    });

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      // Foca o primeiro campo inválido (acessibilidade + UX).
      const firstIndex = Math.min(...Object.keys(nextErrors).map(Number));
      const firstField = ["question", "answer", "validFrom", "validUntil"].find(
        (f) => nextErrors[firstIndex]?.[f],
      );
      if (firstField) {
        const el = document.getElementById(`${uid}-${firstIndex}-${firstField}`);
        el?.focus?.();
      }
      return;
    }

    setErrors({});
    onSave?.(drafts.map(toCleanItem));
  };

  return (
    <div className={styles.wrapper}>
      {drafts.length === 0 ? (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>Nenhuma pergunta cadastrada ainda</p>
          <p className={styles.emptyText}>
            Cadastre respostas prontas para o assistente. Você pode criar FAQs
            perenes ou campanhas com data de validade (ex.: colônia de férias).
          </p>
        </div>
      ) : (
        <ul className={styles.list}>
          {drafts.map((item, index) => {
            const itemErrors = errors[index] || {};
            const isCampaign = item.type === "campaign";
            const status = computeFaqStatus(item);
            const rowId = `${uid}-${index}`;
            const qId = `${rowId}-question`;
            const aId = `${rowId}-answer`;
            const fromId = `${rowId}-validFrom`;
            const untilId = `${rowId}-validUntil`;

            return (
              <li key={index} className={styles.card}>
                <div className={styles.cardHeader}>
                  <span
                    className={`${styles.badge} ${styles[TONE_CLASS[status.tone]]}`}
                    role="status"
                  >
                    {status.label}
                  </span>
                  <button
                    type="button"
                    className={styles.removeBtn}
                    onClick={() => handleRemove(index)}
                    aria-label={`Remover pergunta ${index + 1}`}
                  >
                    Remover
                  </button>
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor={qId}>
                    Pergunta
                  </label>
                  <input
                    id={qId}
                    type="text"
                    className={`${styles.input} ${itemErrors.question ? styles.invalid : ""}`}
                    value={item.question}
                    maxLength={200}
                    placeholder="Ex.: Vocês têm colônia de férias?"
                    onChange={(e) =>
                      handleFieldChange(index, "question", e.target.value)
                    }
                    aria-invalid={itemErrors.question ? "true" : "false"}
                    aria-describedby={
                      itemErrors.question ? `${qId}-error` : undefined
                    }
                  />
                  {itemErrors.question && (
                    <p id={`${qId}-error`} className={styles.errorText} role="alert">
                      {itemErrors.question}
                    </p>
                  )}
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor={aId}>
                    Resposta
                  </label>
                  <textarea
                    id={aId}
                    className={`${styles.textarea} ${itemErrors.answer ? styles.invalid : ""}`}
                    rows={3}
                    value={item.answer}
                    maxLength={500}
                    placeholder="Ex.: Sim! Nossa colônia de férias acontece em julho..."
                    onChange={(e) =>
                      handleFieldChange(index, "answer", e.target.value)
                    }
                    aria-invalid={itemErrors.answer ? "true" : "false"}
                    aria-describedby={
                      itemErrors.answer ? `${aId}-error` : undefined
                    }
                  />
                  {itemErrors.answer && (
                    <p id={`${aId}-error`} className={styles.errorText} role="alert">
                      {itemErrors.answer}
                    </p>
                  )}
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor={`${rowId}-type`}>
                    Tipo
                  </label>
                  <select
                    id={`${rowId}-type`}
                    className={styles.select}
                    value={item.type}
                    onChange={(e) => handleTypeChange(index, e.target.value)}
                  >
                    <option value="faq">Pergunta frequente (sempre ativa)</option>
                    <option value="campaign">Campanha (com validade)</option>
                  </select>
                </div>

                {isCampaign && (
                  <div className={styles.dateRow}>
                    <div className={styles.field}>
                      <label className={styles.label} htmlFor={fromId}>
                        Início (opcional)
                      </label>
                      <input
                        id={fromId}
                        type="date"
                        className={`${styles.input} ${itemErrors.validFrom ? styles.invalid : ""}`}
                        value={item.validFrom || ""}
                        onChange={(e) =>
                          handleFieldChange(index, "validFrom", e.target.value)
                        }
                        aria-invalid={itemErrors.validFrom ? "true" : "false"}
                        aria-describedby={
                          itemErrors.validFrom ? `${fromId}-error` : undefined
                        }
                      />
                      {itemErrors.validFrom && (
                        <p
                          id={`${fromId}-error`}
                          className={styles.errorText}
                          role="alert"
                        >
                          {itemErrors.validFrom}
                        </p>
                      )}
                    </div>

                    <div className={styles.field}>
                      <label className={styles.label} htmlFor={untilId}>
                        Validade / Fim
                      </label>
                      <input
                        id={untilId}
                        type="date"
                        className={`${styles.input} ${itemErrors.validUntil ? styles.invalid : ""}`}
                        value={item.validUntil || ""}
                        onChange={(e) =>
                          handleFieldChange(index, "validUntil", e.target.value)
                        }
                        aria-invalid={itemErrors.validUntil ? "true" : "false"}
                        aria-describedby={
                          itemErrors.validUntil ? `${untilId}-error` : undefined
                        }
                      />
                      {itemErrors.validUntil && (
                        <p
                          id={`${untilId}-error`}
                          className={styles.errorText}
                          role="alert"
                        >
                          {itemErrors.validUntil}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className={styles.actions}>
        <button type="button" className={styles.addBtn} onClick={handleAdd}>
          + Adicionar pergunta
        </button>
        <ButtonSave
          type="button"
          isLoading={saving}
          disabled={!isDirty || saving}
          onClick={handleSave}
        >
          Salvar FAQ e campanhas
        </ButtonSave>
      </div>
    </div>
  );
}
